import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWakeLock } from 'react-screen-wake-lock';
import {
  LamelaManualCalibration,
  LamelaTiltCalibration,
} from 'lavva.exalushome.extalife/build/js/Devices/DeviceServices/FacadeBlindService/ZfBidiConfigParams';
import { Status } from 'lavva.exalushome/build/js/DataFrame';
import { CustomBackdrop, Header, NavHeader, Page, SubmitButton } from '../../../../../../../components';
import { usePreventBack } from '../../../../../../../hooks/router/use-navigation-guard';
import { ROUTES } from '../../../../../../../routes/routes';
import { ChannelBoxIcon } from '../../../../../components/channel/box-icon';
import { useFacadeControl } from '../../../../../components/channel/hooks/use-facade-control';
import { useHandleDataFrameErrors } from '../../../../../hooks/errors';
import { useConfigurationService } from '../../../../device-details/hooks/use-configuration-service';
import { useCalibration } from '../../common/hooks/use-calibration';
import './index.scss';

type ZfBidiCalibrationStep3Props = {
  setStep: Dispatch<SetStateAction<number>>;
  setMovementService: Dispatch<SetStateAction<LamelaManualCalibration | undefined>>;
  setTiltService: Dispatch<SetStateAction<LamelaTiltCalibration | undefined>>;
};

const ZfBidiCalibrationStep3: React.FC<ZfBidiCalibrationStep3Props> = ({
  setStep,
  setMovementService,
  setTiltService,
}) => {
  const { t: td } = useTranslation('device-add');
  const { channel, navigate, t, getZfBidiConfigService } = useCalibration();
  const { stop } = useFacadeControl(channel);
  const { handleGetFacadeParamsResponse } = useConfigurationService();
  const { handleError } = useHandleDataFrameErrors();
  const { request, release } = useWakeLock();
  const [loading, setLoading] = useState<boolean>(false);
  usePreventBack(loading, t('exalus.params.ZfBidiParams.calibrationFlow.noBackMessage'));

  useEffect(() => {
    request();

    return () => {
      release();
    };
  }, []);

  const handleStart = async () => {
    if (channel) {
      setLoading(true);
      const configService = await getZfBidiConfigService();
      const result = await configService.LamelaTiltCalibration(channel?.GetDevice(), channel.Number);

      handleGetFacadeParamsResponse<LamelaTiltCalibration>(result, async () => {
        setTiltService(result as LamelaTiltCalibration);
        const initResult = await (result as LamelaTiltCalibration).InitCalibrationAsync();

        handleGetFacadeParamsResponse<Status>(initResult, () => {
          if (initResult === Status.OK) {
            setMovementService((result as LamelaTiltCalibration).LamelaManualCalibration);
            setLoading(false);

            setStep(4);
            navigate(`${ROUTES.FacadeCalibration(channel.ChannelId)}?step=4`);
          } else {
            handleError(initResult as Status);
          }
        });
      });
    }
  };

  const handleStop = () => {
    stop();
    setLoading(false);
  };

  const handleNext = () => {
    setStep(6);
    navigate(`${ROUTES.FacadeCalibration(channel?.ChannelId)}?step=6`, { state: { back: 5 } });
  };

  return (
    <Page
      className="calibration-flow"
      isStickyHeader
      whiteContent
      header={
        <>
          <NavHeader />
          <Header title={t('exalus.calibration')} />
        </>
      }
    >
      {channel && <ChannelBoxIcon channel={channel} className="facade-icon" />}
      <p className="info m-t-40 m-b-24">{t('exalus.params.ZfBidiParams.calibrationFlow.setLamelsDefault')}</p>
      <p className="info--sub m-b-24 tiny">
        {t(`exalus.params.ZfBidiParams.calibrationFlow.${!loading ? 'setLamelsInfo' : 'positionSetInProgress'}`)}
      </p>
      {!loading && (
        <button className="button button--secondary m-t-24" onClick={handleStart}>
          {t('exalus.params.ZfBidiParams.calibrationFlow.start')}
        </button>
      )}
      {!loading && (
        <SubmitButton type="button" className="button" onClick={handleNext} tranparent>
          {t('exalus.params.ZfBidiParams.calibrationFlow.next')}
        </SubmitButton>
      )}
      {loading && <CustomBackdrop loading btnTitle={td('exalus.searchStop')} onClick={handleStop} />}
    </Page>
  );
};

export default ZfBidiCalibrationStep3;
